import { useMemo } from "react"

import { RatingStars } from "@/features/advisors"
import { useChat } from "@/features/chat"
import {
  REPORTS,
  useReportCta,
  useReportStates,
  useUserReports,
  type ReportId,
  type ReportItem,
} from "@/features/today"

import ChevronRight from "@/assets/svg/compatibility/chevron-right.svg?react"

interface ContinueJourneySectionProps {
  /** The report currently open (by id) — excluded from the cross-sell slider. */
  currentId: ReportId
}

/**
 * "Continue your journey" — slider of the other reports.
 * Per the Figma annotation (node 3037-18897): tapping a not-yet-purchased report
 * starts its purchase (the existing starter popover / purchase flow).
 */
export function ContinueJourneySection({ currentId }: ContinueJourneySectionProps) {
  const { userMainSubscriptionId } = useChat()
  const { handleReportCta, isReportPurchasing } = useReportCta({ userMainSubscriptionId })
  const { reports: apiReports } = useUserReports()
  const stateOf = useReportStates()

  const items = useMemo(
    () =>
      REPORTS.filter((r) => r.id !== currentId).map((report) => {
        const { purchased, price } = stateOf(report)
        return { report, purchased, price }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentId, apiReports, stateOf],
  )

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-display text-2xl font-medium text-purple-light">
          Continue your journey
        </h2>
        <p className="font-body text-base text-purple-light/70">
          Explore more personalized reports to uncover new insights about love, timing, places, and
          your path
        </p>
      </div>

      <div className="-mx-4 flex gap-4 overflow-x-auto px-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map(({ report, purchased, price }) => (
          <BookCard
            key={report.title}
            report={report}
            purchased={purchased}
            price={price}
            disabled={isReportPurchasing(report)}
            onCta={() => handleReportCta(report, purchased)}
          />
        ))}
      </div>
    </section>
  )
}

function BookCard({
  report,
  purchased,
  price,
  disabled,
  onCta,
}: {
  report: ReportItem
  purchased: boolean
  price: number
  disabled: boolean
  onCta: () => void
}) {
  return (
    <button
      type="button"
      onClick={onCta}
      disabled={disabled}
      className="relative flex w-[320px] shrink-0 flex-col overflow-hidden rounded-[20px] bg-purple-light/10 text-left disabled:opacity-70"
    >
      {!purchased && (
        <span className="absolute left-0 top-0 z-[1] flex items-center rounded-br-[20px] bg-purple-light/10 px-4 py-1.5 font-body text-xs font-semibold text-white">
          ${price}
        </span>
      )}

      <div className="flex h-40 w-full items-center justify-center">
        <img src={report.hugeCover} alt={report.title} className="h-full w-[280px] object-contain" />
      </div>

      <div className="flex items-center justify-between gap-2 p-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="truncate font-display text-base font-medium text-white">{report.title}</p>
          <div className="flex items-center gap-1">
            <RatingStars rating={report.rating} className="h-4 w-20 text-gold" />
            <span className="font-body text-sm font-medium text-gold">{report.rating}</span>
            <span className="font-body text-sm text-purple-light/70">({report.purchaseCount})</span>
          </div>
        </div>

        {purchased ? (
          <span className="flex size-9 shrink-0 items-center justify-center text-purple-light/70 [&_svg]:w-[7px]">
            <ChevronRight />
          </span>
        ) : (
          <span className="flex h-9 shrink-0 items-center justify-center rounded-[80px] bg-green/20 px-6 font-body text-base font-semibold text-green">
            Get
          </span>
        )}
      </div>
    </button>
  )
}
